// AudiencesBlock — side-by-side cards presenting the two big audiences:
// 👤 Amateurs (l'app) · 🏢 Professionnels (iframe + AI + Maps + Academy)
// i18n : dictionnaire inline COPY (fr/en/es/pt).
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { useLang } from "@/lib/i18n";

const COPY = {
  fr: {
    eyebrow: "Pour qui", h2_a: "Deux publics,", h2_b: "une même expérience",
    amateur_eye: "Pour les amateurs",
    amateur_h3: "Une seule app pour explorer et apprendre le vin",
    amateur_items: [
      "Découvrir une appellation",
      "Comprendre un cépage",
      "Poser une question à l\u2019IA",
      "Apprendre quelques minutes par jour",
      "Préparer un voyage œnotouristique",
      "Photographier une bouteille",
      "Construire son carnet de dégustation",
      "Comprendre son profil gustatif",
      "Jouer, tester ses connaissances",
    ],
    amateur_cta: "Ouvrir l'app geoVINUM", amateur_ai: "Découvrir geoVINUM app",
    pro_eye: "Pour les professionnels",
    pro_h3: "Transformer le terroir en expérience digitale",
    pro_items: ["Producteurs", "Cavistes & bars à vin", "Écoles du vin", "Clubs d\u2019œnologie", "Interprofessions", "Acteurs de l\u2019œnotourisme", "Sites spécialisés vin"],
    pro_summary: "Iframe + geoVINUM app + cartes interactives + contenus pédagogiques + expériences.",
    pro_cta: "Voir les offres pros", pro_contact: "Parler à l'équipe",
  },
  en: {
    eyebrow: "For whom", h2_a: "Two audiences,", h2_b: "one experience",
    amateur_eye: "For enthusiasts",
    amateur_h3: "A single app to explore and learn wine",
    amateur_items: [
      "Discover an appellation",
      "Understand a grape variety",
      "Ask the AI a question",
      "Learn a few minutes a day",
      "Prepare a wine trip",
      "Snap a bottle photo",
      "Build a tasting journal",
      "Understand your taste profile",
      "Play, test your knowledge",
    ],
    amateur_cta: "Open the geoVINUM app", amateur_ai: "Discover geoVINUM app",
    pro_eye: "For professionals",
    pro_h3: "Turn terroir into a digital experience",
    pro_items: ["Producers", "Wine merchants & bars", "Wine schools", "Wine clubs", "Trade associations", "Wine-tourism players", "Specialized wine sites"],
    pro_summary: "Iframe + geoVINUM app + interactive maps + educational content + experiences.",
    pro_cta: "See pro offers", pro_contact: "Talk to the team",
  },
  es: {
    eyebrow: "Para quién", h2_a: "Dos públicos,", h2_b: "una misma experiencia",
    amateur_eye: "Para los aficionados",
    amateur_h3: "Una sola app para explorar y aprender vino",
    amateur_items: [
      "Descubrir una denominación",
      "Comprender una variedad",
      "Preguntar a la IA",
      "Aprender unos minutos al día",
      "Preparar un viaje enoturístico",
      "Fotografiar una botella",
      "Construir tu cuaderno de cata",
      "Comprender tu perfil gustativo",
      "Jugar, probar tus conocimientos",
    ],
    amateur_cta: "Abrir la app geoVINUM", amateur_ai: "Descubrir geoVINUM app",
    pro_eye: "Para los profesionales",
    pro_h3: "Transformar el terruño en experiencia digital",
    pro_items: ["Productores", "Tiendas de vino y wine bars", "Escuelas de vino", "Clubes enológicos", "Interprofesiones", "Actores del enoturismo", "Sitios especializados en vino"],
    pro_summary: "Iframe + geoVINUM app + mapas interactivos + contenidos pedagógicos + experiencias.",
    pro_cta: "Ver las ofertas pro", pro_contact: "Hablar con el equipo",
  },
  pt: {
    eyebrow: "Para quem", h2_a: "Dois públicos,", h2_b: "uma mesma experiência",
    amateur_eye: "Para os apreciadores",
    amateur_h3: "Uma única app para explorar e aprender vinho",
    amateur_items: [
      "Descobrir uma denominação",
      "Compreender uma casta",
      "Perguntar à IA",
      "Aprender alguns minutos por dia",
      "Preparar uma viagem enoturística",
      "Fotografar uma garrafa",
      "Construir o seu caderno de prova",
      "Compreender o seu perfil gustativo",
      "Jogar, testar os seus conhecimentos",
    ],
    amateur_cta: "Abrir a app geoVINUM", amateur_ai: "Descobrir geoVINUM app",
    pro_eye: "Para os profissionais",
    pro_h3: "Transformar o terroir em experiência digital",
    pro_items: ["Produtores", "Garrafeiras e wine bars", "Escolas de vinho", "Clubes enológicos", "Interprofissões", "Atores do enoturismo", "Sites especializados em vinho"],
    pro_summary: "Iframe + geoVINUM app + mapas interativos + conteúdos pedagógicos + experiências.",
    pro_cta: "Ver as ofertas pro", pro_contact: "Falar com a equipa",
  },
};

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5 text-[14.5px] leading-[1.5]">
    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[color:var(--gv-pink)] shrink-0" />
    <span>{children}</span>
  </li>
);

const AudiencesBlock = () => {
  const lang = useLang();
  const c = COPY[lang] || COPY.fr;
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="home-audiences">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-4">{c.eyebrow}</div>
          <h2 className="font-display text-[32px] md:text-[44px] leading-[1.1] text-[color:var(--gv-ink)] max-w-2xl mx-auto">
            {c.h2_a} <span className="italic text-[color:var(--gv-pink)]">{c.h2_b}</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Amateurs */}
          <div
            className="bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] rounded-[6px] p-8 md:p-10 reveal"
            data-testid="audience-amateurs"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-full bg-white border border-[color:var(--gv-line)] flex items-center justify-center text-[22px]">👤</div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)]">{c.amateur_eye}</div>
                <h3 className="font-display text-[24px] text-[color:var(--gv-ink)] leading-tight">{c.amateur_h3}</h3>
              </div>
            </div>
            <ul className="space-y-2.5 text-[color:var(--gv-ink)]/85 mb-7 columns-1 sm:columns-2">
              {c.amateur_items.map((it) => <Bullet key={it}>{it}</Bullet>)}
            </ul>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://app.geovinum.io"
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
                data-testid="audience-amateurs-cta"
              >
                {c.amateur_cta} <ExternalLink size={14} />
              </a>
              <Link
                to="/geovinum-ai"
                className="btn-secondary"
                data-testid="audience-amateurs-ai"
              >
                {c.amateur_ai} <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* Pros */}
          <div
            className="bg-[color:var(--gv-ink)] text-white rounded-[6px] p-8 md:p-10 reveal"
            data-testid="audience-pros"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-[22px]">🏢</div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)]">{c.pro_eye}</div>
                <h3 className="font-display text-[24px] text-white leading-tight">{c.pro_h3}</h3>
              </div>
            </div>
            <ul className="space-y-2.5 text-white/85 mb-5 columns-1 sm:columns-2">
              {c.pro_items.map((it) => <Bullet key={it}>{it}</Bullet>)}
            </ul>
            <div className="text-[13.5px] text-white/60 mb-6">
              {c.pro_summary}
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/pro"
                className="btn-primary"
                data-testid="audience-pros-cta"
              >
                {c.pro_cta} <ArrowRight size={14} />
              </Link>
              <Link
                to="/contact?topic=pro"
                className="text-white/85 inline-flex items-center gap-2 text-[14px] hover:text-[color:var(--gv-pink)] px-4 py-2.5"
                data-testid="audience-pros-contact"
              >
                {c.pro_contact} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AudiencesBlock;
